import { Link } from 'react-router-dom'
import PublicLayout from '../../components/layouts/PublicLayout'
import {
  Shield,
  Lock,
  FileCheck,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Clock,
  Eye,
} from 'lucide-react'

const forbiddenStates = [
  {
    title: 'Session Billed Without Authorization',
    description: 'System blocks claim generation if authorization is not active or has insufficient units.',
    icon: XCircle,
  },
  {
    title: 'Session Billed Without Locked Note',
    description: 'Claims can only be generated from notes that have been signed, attested, and locked.',
    icon: XCircle,
  },
  {
    title: 'Overlapping Sessions',
    description: 'System prevents scheduling sessions that overlap for the same client or same staff member.',
    icon: XCircle,
  },
  {
    title: 'Backdated Edits After Lock',
    description: 'Once a note is locked, the original content is immutable. Changes require addendums only.',
    icon: XCircle,
  },
]

const auditDefenseCapabilities = [
  {
    title: 'One-Click Audit Packet Export',
    description: 'Generate complete documentation packages including notes, authorizations, credentials, and timestamps.',
    icon: FileCheck,
  },
  {
    title: 'Immutable Audit Logs',
    description: 'Every PHI access, every edit, every signature—logged with user, timestamp, IP, and action.',
    icon: Eye,
  },
  {
    title: 'Retention Policy Enforcement',
    description: 'Configurable retention by payer and state. Legal holds prevent deletion during disputes.',
    icon: Clock,
  },
  {
    title: 'Staff Credential Tracking',
    description: 'Monitor credential expirations. Block billing if provider credentials are expired.',
    icon: Shield,
  },
]

export default function CompliancePage() {
  return (
    <PublicLayout>
      <div className="bg-gradient-to-b from-primary-50 to-white py-24">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="max-w-2xl">
              <h1 className="text-4xl font-bold tracking-tight text-gray-900 sm:text-5xl">
                Audit Defense by Design
              </h1>
              <p className="mt-6 text-lg text-gray-600">
                Not compliance features added later. Enforcement logic that prevents forbidden states from ever
                existing in your system.
              </p>
            </div>
            <div className="relative">
              <div className="rounded-2xl overflow-hidden shadow-2xl ring-1 ring-gray-200">
                <img
                  src="/images/parent1.png"
                  alt="Compliance-first clinical workflow"
                  className="w-full h-72 object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Forbidden States */}
      <div className="bg-white py-24">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="mx-auto max-w-2xl text-center mb-16">
            <h2 className="text-3xl font-bold tracking-tight text-gray-900">
              Forbidden States the System Blocks
            </h2>
            <p className="mt-4 text-gray-600">
              These conditions cannot exist in our system. Not warnings—hard blocks.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {forbiddenStates.map((state) => (
              <div
                key={state.title}
                className="bg-red-50 border border-red-100 rounded-lg p-6"
              >
                <div className="flex items-start gap-4">
                  <div className="p-2 bg-red-100 rounded-lg">
                    <state.icon className="h-6 w-6 text-red-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-red-900">{state.title}</h3>
                    <p className="mt-2 text-sm text-red-700">{state.description}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Note Locking */}
      <div className="bg-gray-50 py-24">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold tracking-tight text-gray-900">
                Note Locking & Addendum-Only Edits
              </h2>
              <p className="mt-4 text-gray-600">
                When an auditor asks "was this note modified after billing?", your answer
                is definitively NO. The system makes it impossible.
              </p>

              <div className="mt-8 space-y-4">
                <div className="flex items-start gap-3">
                  <CheckCircle className="h-5 w-5 text-green-500 mt-0.5" />
                  <div>
                    <p className="font-medium text-gray-900">RBT Signs Note</p>
                    <p className="text-sm text-gray-600">Content locked, status changes to "Signed"</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="h-5 w-5 text-green-500 mt-0.5" />
                  <div>
                    <p className="font-medium text-gray-900">BCBA Attests</p>
                    <p className="text-sm text-gray-600">Note moves to "Locked" status</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="h-5 w-5 text-green-500 mt-0.5" />
                  <div>
                    <p className="font-medium text-gray-900">Content Hash Calculated</p>
                    <p className="text-sm text-gray-600">SHA-256 hash proves content unchanged</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="h-5 w-5 text-green-500 mt-0.5" />
                  <div>
                    <p className="font-medium text-gray-900">Addendums Only</p>
                    <p className="text-sm text-gray-600">Any additions are separate, timestamped addendums</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-lg p-6 border border-gray-200">
              <div className="space-y-4">
                <div className="flex items-center justify-between pb-4 border-b">
                  <span className="font-medium">Note Status</span>
                  <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm">
                    LOCKED
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Signed by RBT</span>
                  <span className="text-sm">2024-01-15 14:32:00</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Attested by BCBA</span>
                  <span className="text-sm">2024-01-15 16:45:00</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Locked at</span>
                  <span className="text-sm">2024-01-15 16:45:00</span>
                </div>
                <div className="pt-4 border-t">
                  <span className="text-sm text-gray-600">Content Hash</span>
                  <p className="font-mono text-xs text-gray-500 mt-1 break-all">
                    a7f2b8c9d4e5f6a1b2c3d4e5f6a7b8c9d0e1f2a3b4c5d6e7f8a9b0c1d2e3f4a5
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Audit Defense Capabilities */}
      <div className="bg-white py-24">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="mx-auto max-w-2xl text-center mb-16">
            <h2 className="text-3xl font-bold tracking-tight text-gray-900">
              When the Auditor Calls
            </h2>
            <p className="mt-4 text-gray-600">
              Tools to respond quickly and confidently to audit requests
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {auditDefenseCapabilities.map((capability) => (
              <div
                key={capability.title}
                className="bg-primary-50 border border-primary-100 rounded-lg p-6"
              >
                <div className="flex items-start gap-4">
                  <div className="p-2 bg-primary-100 rounded-lg">
                    <capability.icon className="h-6 w-6 text-primary-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-primary-900">{capability.title}</h3>
                    <p className="mt-2 text-sm text-primary-700">{capability.description}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* CTA */}
      <div className="bg-gray-900 py-24">
        <div className="mx-auto max-w-7xl px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold tracking-tight text-white">
            Ready for Audit-Proof Operations?
          </h2>
          <p className="mt-4 text-lg text-gray-300">
            See how our compliance architecture protects your practice.
          </p>
          <div className="mt-8">
            <Link to="/contact" className="btn bg-white text-gray-900 hover:bg-gray-100 text-base px-6 py-3">
              Request Demo
            </Link>
          </div>
        </div>
      </div>
    </PublicLayout>
  )
}
